<template>
  <div class="advanced-team-picker-tree" :class="elClasses">
    <div v-if="loadedTeams">
      <AdvancedTeamPickerRow
        v-for="team in loadedTeams.slice(0, teamsDisplayedRange.value)"
        :key="`root${team.id}`"
        :team="team"
        :for-display-only="forDisplayOnly"
        :allow-change-hierarchy="allowChangeHierarchy"
        :min="minimalTree"
        :disable-higher-hierarchy="disableHigherHierarchy"
        :disable-inaccessible="disableInaccessible"
        :config="config"
        @edit="teamsEdited"
        @hierarchyChange="(e) => { $emit('hierarchyChange', e) }"
      />
    </div>
    <div
      v-if="teamsDisplayedRange.show"
      class="advanced-team-picker-tree__show-more-teams"
      @click="displayOthersTeams"
    >
      {{ $t('message.LOAD_MORE') }}
    </div>
    <LoadingOverlay :value="loadedTeams === null" />
  </div>
</template>

<script>
/**
 * tree-like list of selectable teams.
 * Tree is built according to UserTeamAccess from the database.
 *
 * @vue-prop {Array} v-model - array of ids
 * @vue-prop {Boolean} displayAllTeams - If false (default), display only the current team and teams belox
 * @vue-prop {Boolean} disabled - Should this component be disabled
 * @vue-prop {Boolean} singleValue - Allow only one selected team
 * @vue-prop {Boolean} config - Fetch teams from config account instead of normal account
 * @vue-prop {Boolean} forDisplayOnly - If true, remove all checkboxes and expand arrows
 * @vue-prop {Boolean} allowChangeHierarchy - If true and forDisplayOnly true, allows reorganizing the teams hierarchy
 * @vue-prop {Boolean} disableHigherHierarchy - All teams with a higher hierarchy level cannot be selected
 * @vue-prop {Boolean} disableInaccessible - Inaccessible cannot be selected. A team is accessible if it has an
 *                                              access throuh the hierarchy tree or through user accesses
 *
 * @vue-event {String} input - Emitted when the value changes
 * @vue-event {Object} hierarchyChange - Emitted when the user reorganises the teams in the tree
 */

import $ from 'jquery';
import { cloneDeep } from 'lodash';
// Vuex
import { mapState, mapGetters } from 'vuex';
// Vuex utils
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Components
import AdvancedTeamPickerRow from '@/components/advanced-team-picker-row.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
// Utils
import IAHelp from '@/utils/classes/IAHelp';
import { treeTraversal } from '@/utils/main';
import { ADV_TEAM_PICKER_INITIAL_VALUES } from '@/utils/advanced-team-picker-utils';

export default {

  name: 'AdvancedTeamPickerTree',

  components: {
    AdvancedTeamPickerRow,
    LoadingOverlay,
  },

  props: {
    value: {
      type: Array,
      required: true,
    },
    displayAllTeams: Boolean,
    useDemoTeamsTree: Boolean,
    disabled: Boolean,
    singleValue: Boolean,
    config: Boolean,
    forDisplayOnly: Boolean,
    allowChangeHierarchy: Boolean,
    disableHigherHierarchy: Boolean,
    disableInaccessible: Boolean,
  },

  data() {
    return {
      elWidth: null,
      teamsDisplayedRange: {
        value: ADV_TEAM_PICKER_INITIAL_VALUES,
        show: true,
      },
      loadedTeams: null,
    };
  },

  computed: {
    /** Array: Tree-like array => each element hasa 'teams' property which is an array of other teams */
    teamsTree() {
      if (this.useDemoTeamsTree) {
        return treeTraversal(cloneDeep(IAHelp.demoTeamsTree), 'teams', (team) => {
          this.$set(team, 'selected', this.value.includes(team.id));
        });
      }
      const tree = this.displayAllTeams ? this.allTeamsTree : this.teamsTreeFromCurrent;
      return treeTraversal(cloneDeep(tree), 'teams', (team) => {
        this.$set(team, 'selected', this.value.includes(team.id));
      });
    },
    /** Boolean: Whether to display the tree as a minimal view */
    minimalTree() {
      return this.elWidth < (this.$store.getters.fontSize * 40);
    },
    /** Object: Classes aplied to $el */
    elClasses() {
      return {
        // When operating as a displayOnly components, add the CSS behaviour of the children wrapper
        'advanced-team-picker-row__children-wrapper': this.forDisplayOnly,
        'advanced-team-picker-row__children-wrapper--for-display': this.forDisplayOnly,
        'advanced-team-picker-row__children-wrapper--min': this.forDisplayOnly && this.minimalTree,
      };
    },
    ...mapState({
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    ...mapGetters([
      'teamsTreeFromCurrent',
      'allTeamsTree',
    ]),
  },

  watch: {
    teamsTree() {
      this.loadedTeams = this.teamsTree;
    },
  },

  created() {
    if (!this.allTeamsLoading) {
      this.$store.dispatch(FETCH_TEAMS, { config: this.config ? 1 : undefined });
    }
    if (this.teamsDisplayedRange.value >= this.teamsTree.length) {
      this.teamsDisplayedRange.show = false;
    }
  },

  mounted() {
    this.elWidth = this.$el.offsetWidth;
    $(window).on(this.resizeEventName, () => {
      this.elWidth = this.$el.offsetWidth;
    });
  },

  methods: {
    /** One or several teams have been (un)selected */
    teamsEdited(teams) {
      if (!(teams instanceof Object)) {
        return;
      }
      if (!(teams instanceof Array)) {
        teams = [teams];
      }

      if (this.singleValue) {
        const firstSelected = teams.find(team => team.selected);
        if (firstSelected) {
          this.$emit('input', [firstSelected.id]);
        }
      } else {
        const valueCopy = cloneDeep(this.value);
        // Add or remove the team ids from value and emit
        teams.forEach((team) => {
          const index = valueCopy.indexOf(team.id);
          if (team.selected && index === -1) {
            valueCopy.push(team.id);
          } else if (!team.selected && index > -1) {
            valueCopy.splice(index, 1);
          }
        });
        this.$emit('input', valueCopy);
      }
    },
    /** Display more teams */
    displayOthersTeams() {
      this.teamsDisplayedRange.value += ADV_TEAM_PICKER_INITIAL_VALUES;
      if (this.teamsDisplayedRange.value >= this.teamsTree.length) {
        this.teamsDisplayedRange.show = false;
      }
    },
  },
};
</script>

<style lang="scss">
  .advanced-team-picker-tree {
    &__show-more-teams {
      text-align: center;
      margin: 5px 0px 15px 25px;
      padding: 0px 6px;
      width: 100px;
      border-radius: 4px;
      background-color: #dfdfdf;
      font-size: 12px;
      box-shadow: 1px 1.5px 2px rgba(140, 138, 138, 0.8);
      transition: .2s;
      &:hover {
        color: rgb(111, 110, 110);
        background-color:  #c2c2c2e4;
        box-shadow: 1px 1.5px 2px rgba(140, 138, 138, 0.8);
        cursor: pointer;
      }
    }
  }
</style>
